import React from "react";
import * as Avatar from "@radix-ui/react-avatar";
import { getInitials } from "@/utils/getInitials";
import { cn } from "@/lib/utils";
import { getAvatarUrl } from "@/lib/userApi";
import { calculateLevelAndProgress } from "@/utils/calculateLevel";
import { IAuthorData } from "@/lib/community";
import { useTranslation } from "react-i18next";

interface IProps {
  userData: IAuthorData;
  className?: string;
  score?: number;
  showLevel?: boolean;
  showProgress?: boolean;
  size?: "sm" | "md" | "lg" | "xxl" | "xl";
}

const sizeClasses = {
  sm: "w-8 h-8",
  md: "w-10 h-10",
  lg: "w-12 h-12",
  xxl: "w-20 h-20 md:w-24 md:h-24 lg:w-26 lg:h-26",
  xl: "w-32 h-32 md:w-48 md:h-48 lg:w-64 lg:h-64",
};

const levelSizeClasses = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base",
  xxl: "text-lg",
  xl: "text-xl",
};

export default function CustomAvatar({
  userData,
  className,
  showLevel = true,
  showProgress = true,
  size = "md",
}: IProps) {
  const { t } = useTranslation();
  if (!userData) return null;

  const { username, avatar, user_rating } = userData;
  const { level, currentExp, maxExp } = calculateLevelAndProgress(
    user_rating?.score || 0
  );
  const progressPercentage = maxExp > 0 ? (currentExp / maxExp) * 100 : 0;

  const progressSize =
    size === "sm"
      ? 40
      : size === "md"
      ? 48
      : size === "lg"
      ? 56
      : size === "xxl"
      ? 120
      : 144;
  const strokeWidth =
    size === "sm"
      ? 3
      : size === "md"
      ? 4
      : size === "lg"
      ? 5
      : size === "xxl"
      ? 6
      : 6;
  const radius = (progressSize - strokeWidth) / 2;

  const arcLength = 2 * Math.PI * radius;
  const strokeDashoffset = arcLength - (progressPercentage / 100) * arcLength;

  return (
    <div
      className={cn(
        "relative inline-flex items-center justify-center",
        size === "xl" &&
          showProgress &&
          "w-36 h-36 md:w-52 md:h-52 lg:w-72 lg:h-72"
      )}
      style={{
        width: showProgress && size !== "xl" ? progressSize : undefined,
        height: showProgress && size !== "xl" ? progressSize : undefined,
      }}
    >
      {showProgress && (
        <svg
          className={cn(
            "absolute top-0 left-0 transform -rotate-90",
            size === "xl" ? "w-36 h-36 md:w-52 md:h-52 lg:w-72 lg:h-72" : ""
          )}
          width={size !== "xl" ? progressSize : undefined}
          height={size !== "xl" ? progressSize : undefined}
          viewBox={
            size === "xl" ? `0 0 ${progressSize} ${progressSize}` : undefined
          }
        >
          <circle
            cx={progressSize / 2}
            cy={progressSize / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={`${arcLength} ${2 * Math.PI * radius}`}
            strokeDashoffset={0}
            strokeLinecap="round"
            className="text-gray-200"
          />
          <circle
            cx={progressSize / 2}
            cy={progressSize / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={`${arcLength} ${2 * Math.PI * radius}`}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="text-blue-500 transition-all duration-300 ease-in-out"
          />
        </svg>
      )}

      <Avatar.Root
        className={cn(
          "inline-flex items-center justify-center rounded-full bg-gray-200 overflow-hidden",
          showProgress
            ? "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
            : "relative",
          sizeClasses[size],
          className
        )}
      >
        <Avatar.Image
          className="w-full h-full object-cover"
          src={getAvatarUrl(avatar)}
          alt={t("community.customAvatar.userAvatar")}
        />
        <Avatar.Fallback
          className={cn(
            "text-sm font-medium text-gray-600",
            `${size === "xl" ? "text-4xl md:text-8xl" : ""}`
          )}
        >
          {getInitials(username)}
        </Avatar.Fallback>
      </Avatar.Root>

      {showLevel && (
        <div
          className={cn(
            "absolute bg-blue-500 text-white rounded-full flex items-center justify-center px-1",
            size === "xl"
              ? "bottom-10 md:bottom-20 -right-4 md:-right-1 min-w-[40px] h-10"
              : "-bottom-0 -right-1 min-w-[20px] h-5"
          )}
        >
          <span
            className={cn("font-bold leading-none", levelSizeClasses[size])}
          >
            {level}
          </span>
        </div>
      )}
    </div>
  );
}
